import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, MessageCircle, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { Magnetic } from "@/components/Magnetic";

const line1 = "Just bring your pots and pans,".split(" ");
const line2 = "and start cooking.".split(" ");

const parent = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.4 } } };
const word = {
  hidden: { y: "115%", rotate: 4 },
  show: { y: 0, rotate: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
      {videoFailed ? (
        <img
          src="/assets/video/hero-poster.jpg"
          alt="Inside the Royal Express cloud kitchen, Dubai Investment Park"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="hero-poster-fallback"
        />
      ) : (
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/video/hero-poster.jpg"
          onError={() => setVideoFailed(true)}
        >
          <source src="/assets/video/hero-loop.webm" type="video/webm" />
          <source src="/assets/video/hero-loop.mp4" type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-[#141416]" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" aria-hidden="true" />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-8 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-4 text-[11px] font-semibold uppercase tracking-[0.34em] text-gold md:text-xs"
          data-testid="hero-eyebrow"
        >
          Premium Cloud Kitchen · Dubai Investment Park, UAE
        </motion.p>

        <motion.p
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          className="font-script text-4xl text-gold-light md:text-5xl"
          data-testid="hero-script"
        >
          Royal Express
        </motion.p>

        <motion.h1
          variants={parent}
          initial="hidden"
          animate="show"
          className="text-shadow-hero mt-3 max-w-4xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
          data-testid="hero-headline"
        >
          {line1.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden pb-1 align-bottom">
              <motion.span variants={word} className="inline-block">{w}&nbsp;</motion.span>
            </span>
          ))}
          <span className="block">
            {line2.map((w, i) => (
              <span key={i} className="inline-block overflow-hidden pb-2 align-bottom">
                <motion.span variants={word} className="inline-block font-semibold italic text-gold">{w}&nbsp;</motion.span>
              </span>
            ))}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-6 max-w-xl text-sm font-light leading-relaxed text-white/75 md:text-base"
          data-testid="hero-subcopy"
        >
          A fully-licensed, move-in-ready commercial kitchen built for growing food brands and passionate chefs.
          No licensing formalities, no fit-out delays — just a kitchen that's ready to go.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Magnetic>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToId("#contact"); }}
              data-testid="hero-partner-cta"
              className="inline-flex rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black shadow-[0_0_32px_rgba(212,175,55,0.35)] transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_44px_rgba(212,175,55,0.5)]"
            >
              Partner With Us
            </a>
          </Magnetic>
          <Magnetic>
            <a
              href="#kitchens"
              onClick={(e) => { e.preventDefault(); scrollToId("#kitchens"); }}
              data-testid="hero-explore-cta"
              className="inline-flex rounded-full border border-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold/10"
            >
              Explore The Facility
            </a>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3 border-t border-white/10 pt-6"
        >
          <a href="tel:+97148858977" data-testid="hero-phone-link" className="flex items-center gap-2 text-xs tracking-wider text-white/70 transition-colors duration-300 hover:text-gold">
            <Phone size={14} className="text-gold" /> 04 885 8977
          </a>
          <a href="https://wa.me/971529292511" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-link" className="flex items-center gap-2 text-xs tracking-wider text-white/70 transition-colors duration-300 hover:text-gold">
            <MessageCircle size={14} className="text-gold" /> 052 929 2511
          </a>
          <a href="mailto:arun@rmefandbeqpt.com" data-testid="hero-email-link" className="flex items-center gap-2 text-xs tracking-wider text-white/70 transition-colors duration-300 hover:text-gold">
            <Mail size={14} className="text-gold" /> arun@rmefandbeqpt.com
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden flex-col items-end gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Scroll to explore</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={18} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
